import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "components/translations/useTranslation";
import { useDispatch } from "react-redux";
import Comment from "components/input/Comment.component";
import OKCancelButtons from "components/modals/OKCancelButtons";
import { MODAL_CONTENT_TAB_INDEX } from "utils/constants";
import { SendForApprovalProps, SendForApprovalActionData } from "types/modals";
import { sendDocumentForApproval, simulateWorkflowGraph } from "components/modals/modal.actions";
import { Notification, NotificationType } from "components/common/Notification.component";
import DisplayProgress from "components/common/displayProgress";
import { PATH_TO_DOCUMENT_OVERVIEW } from "components/router/paths";
import WorkflowDetailsWizard
    from "components/taskdetails-remake/panels/workflowdetails/WorkflowDetailsWizard.component.jsx";
import { SimulatorErrorsLog } from "components/workflowdetails/simulator/Simulator.component.jsx";
import { toDisplayError } from "utils/errorHandle.function";

interface WorkflowGraph {
    steps: Array<Record<string, any>>;
    success: boolean;
    log?: { children: Array<Record<string, any>> };
}

const SendForApproval = ({ more, closeModal, loopFocus, errorCallback, initialFocusRef }: SendForApprovalProps): React.ReactElement => {
    const { translate } = useTranslation();
    const dispatch = useDispatch();

    const [comment, setComment] = useState("");
    const [displayError, setDisplayError] = useState<Record<string, any> | null>(null);
    const [actionRunning, setActionRunning] = useState(more.tasks.length === 1);
    const [loadingSimulator, setLoadingSimulator] = useState(more.tasks.length === 1);
    const [workflowGraph, setWorkflowGraph] = useState<WorkflowGraph | null>(null);
    const [warning, setWarning] = useState<string | undefined>(undefined);

    useEffect(() => {
        if (more.tasks.length === 1) {
            dispatch(simulateWorkflowGraph(
                more.tasks[0].key,
                more.companyId,
                (response: WorkflowGraph) => {
                    setWorkflowGraph(response);
                    setLoadingSimulator(false);
                    setActionRunning(false);
                },
                (error: { errorMessages?: string }) => {
                    setWarning(error.errorMessages);
                    setLoadingSimulator(false);
                    setActionRunning(false);
                },
            ));
        }
    }, [more.tasks, more.companyId, dispatch]);

    const handleSuccess = useCallback(() => {
        const taskIds = more.tasks.map((task) => task.key);
        closeModal();
        more.handledTaskCallback(taskIds, more.navigate);
        more.navigate(PATH_TO_DOCUMENT_OVERVIEW);
    }, [more, closeModal]);

    const handleError = useCallback((error: Record<string, any>) => {
        setDisplayError(toDisplayError(error));
        setActionRunning(false);
    }, []);

    const handleOk = useCallback(() => {
        setActionRunning(true);
        setDisplayError(null);

        const taskIds = more.tasks.map((task) => task.key);

        const data: SendForApprovalActionData = {
            companyId: more.companyId,
            taskIds,
            comment,
        };

        dispatch(sendDocumentForApproval(data, handleSuccess, handleError));
    }, [comment, more, dispatch, handleSuccess, handleError]);

    const handleCommentChange = useCallback((value: string) => {
        setComment(value);
        setDisplayError(null);
    }, []);

    const okEnabled = !actionRunning && !warning;

    return (
        <>
            <div className="ga-modal__content">
                {displayError && (
                    <div className="my-16">
                        <Notification
                            type={NotificationType.ERROR}
                            title={displayError.errorTitle}
                            description={displayError.errorMessages}
                        />
                    </div>
                )}
                <Comment
                    ref={initialFocusRef}
                    id="comment"
                    inputHint={translate("popUp.approve.commentHint")}
                    translate={translate}
                    tabIndex={MODAL_CONTENT_TAB_INDEX}
                    value={comment}
                    propagateValue={handleCommentChange}
                    label={translate("popUp.approve.commentLabel")}
                />
                {more.tasks.length === 1 && (
                    <div>
                        {workflowGraph ? (
                            <div>
                                <WorkflowDetailsWizard data={workflowGraph.steps} hideSkippedSteps={true} />
                                {workflowGraph.success === false && workflowGraph.log && (
                                    <SimulatorErrorsLog
                                        nodes={workflowGraph.log.children}
                                        bsClass={"overflow-auto send-for-approval-log mb-2"}
                                        title={translate("workflowDetails.simulator.errorLog")}
                                    />
                                )}
                                <div className="my-3">
                                    <Notification
                                        type={workflowGraph.success ? NotificationType.SUCCESS : NotificationType.ERROR}
                                        description={
                                            workflowGraph.success
                                                ? translate("popUp.sendforapproval.successText")
                                                : translate("popUp.sendforapproval.errorText")
                                        }
                                    />
                                </div>
                            </div>
                        ) : (
                            <div>
                                <div className="simulator col-md-12 mt-2 px-0 panel panel-default">
                                    {loadingSimulator && (
                                        <DisplayProgress message={translate("popUp.sendforapproval.runningSimulation")} />
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
            <OKCancelButtons
                tabIndex={MODAL_CONTENT_TAB_INDEX + 1}
                okEnabled={okEnabled}
                okCallback={handleOk}
                okText={translate("popUp.ok")}
                cancelEnabled={true}
                cancelCallback={closeModal}
                cancelText={translate("popUp.cancel")}
                loopFocus={loopFocus}
            />
        </>
    );
};

export default SendForApproval;
